import { getSampleRegistrationSchema } from '../schemas/schema-utils'
import { findSchemaDefinition, mergeSchemas, RJSFSchema } from '@rjsf/utils'

export type SchemaProperty = {
  /** The JSON Schema property key (e.g. "pregnant") */
  propertyKey: string
  /** Human-readable title from the definition (e.g. "Drektig") */
  title?: string
  /** JSON Schema type string (e.g. "string", "integer") */
  type?: string
  /** All possible enumerated values across all conditional branches */
  enumValues?: string[]
}

/**
 * Resolve a JSON Schema $ref of the form "#/block/key" against the root schema.
 * Returns undefined for missing refs (does not throw).
 * Delegates to findSchemaDefinition from @rjsf/utils which also handles
 * nested $refs in the resolved sub-schema.
 */
export const resolveRef = (
  ref: string,
  root: Record<string, unknown>
): Record<string, unknown> | undefined => {
  if (!ref.startsWith('#/')) return undefined
  try {
    const result = findSchemaDefinition(ref, root as RJSFSchema)
    return typeof result === 'object' && result !== null
      ? (result as Record<string, unknown>)
      : undefined
  } catch {
    return undefined
  }
}

/**
 * Resolve a property definition against the root schema, merging any $ref
 * content with inline overrides (inline values take priority).
 * Uses mergeSchemas from @rjsf/utils for correct deep-merge semantics,
 * including deduplication of the required array.
 */
const resolveDef = (
  def: Record<string, unknown>,
  root: Record<string, unknown>
): Record<string, unknown> => {
  const ref = def['$ref'] as string | undefined
  if (!ref) return def
  const base = resolveRef(ref, root) ?? {}
  return mergeSchemas(base, def) as Record<string, unknown>
}

/** Strip RJSF-internal and admin-only keys (x-*, ui:) recursively. */
const stripInternalKeys = (obj: unknown): unknown => {
  if (Array.isArray(obj)) return obj.map(stripInternalKeys)
  if (obj != null && typeof obj === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (k.startsWith('x-') || k.startsWith('ui:')) continue
      out[k] = stripInternalKeys(v)
    }
    return out
  }
  return obj
}

const isCollectableLocalRef = (
  key: string,
  value: unknown,
  visited: Set<string>
): value is string => {
  return (
    key === '$ref' &&
    typeof value === 'string' &&
    value.startsWith('#/') &&
    !visited.has(value)
  )
}

const collectResolvedRef = (
  ref: string,
  root: Record<string, unknown>,
  groups: Record<string, Record<string, unknown>>,
  visited: Set<string>
): void => {
  visited.add(ref)

  const [blockKey, entryName] = ref.slice(2).split('/')
  if (!blockKey || !entryName) return

  const resolved = resolveRef(ref, root)
  if (resolved == null) return

  if (!groups[blockKey]) {
    groups[blockKey] = {}
  }

  groups[blockKey][entryName] = resolved
  collectRefs(resolved, root, groups, visited)
}

/**
 * Transitively walk obj, collecting every local $ref into groups keyed by
 * block name (e.g. "definitions", "conditionals") then entry name.
 * visited prevents infinite loops.
 */
const collectRefs = (
  obj: unknown,
  root: Record<string, unknown>,
  groups: Record<string, Record<string, unknown>>,
  visited: Set<string>
): void => {
  if (Array.isArray(obj)) {
    obj.forEach((item) => collectRefs(item, root, groups, visited))
    return
  }
  if (obj == null || typeof obj !== 'object') return
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (isCollectableLocalRef(k, v, visited)) {
      collectResolvedRef(v, root, groups, visited)
      continue
    }

    collectRefs(v, root, groups, visited)
  }
}

/** Extract enum values from a property definition. */
const extractEnumValues = (
  def: Record<string, unknown>
): string[] | undefined => {
  if (Array.isArray(def['enum'])) return (def['enum'] as unknown[]).map(String)
  const oneOf = def['oneOf'] as Record<string, unknown>[] | undefined
  if (!Array.isArray(oneOf) || oneOf.length === 0) return undefined
  const vals = oneOf
    .filter((v) => v != null && 'const' in v)
    .map((v) => String(v['const']))
  return vals.length > 0 ? vals : undefined
}

/**
 * Recursively collect all properties from an if/then/else tree, ignoring
 * conditions. Merges enum values across branches for the same property key.
 */
const collectConditionalProperties = (
  node: Record<string, unknown>,
  root: Record<string, unknown>,
  results: Map<string, SchemaProperty>
): void => {
  const thenClause = node['then'] as Record<string, unknown> | undefined
  const elseClause = node['else'] as Record<string, unknown> | undefined

  const collectProps = (props: Record<string, Record<string, unknown>>) => {
    for (const [key, rawDef] of Object.entries(props)) {
      const def = resolveDef(rawDef, root)
      const existing = results.get(key)
      const newEnums = extractEnumValues(def)
      if (existing) {
        if (newEnums) {
          results.set(key, {
            ...existing,
            enumValues: Array.from(
              new Set([...(existing.enumValues ?? []), ...newEnums])
            )
          })
        }
      } else {
        results.set(key, {
          propertyKey: key,
          title: def['title'] as string | undefined,
          type: def['type'] as string | undefined,
          enumValues: newEnums
        })
      }
    }
  }

  const thenProps = thenClause?.['properties'] as
    Record<string, Record<string, unknown>> | undefined
  if (thenProps) collectProps(thenProps)

  if (elseClause) {
    if ('if' in elseClause) {
      collectConditionalProperties(elseClause, root, results)
    } else {
      const elseProps = elseClause['properties'] as
        Record<string, Record<string, unknown>> | undefined
      if (elseProps) collectProps(elseProps)
    }
  }
}

const collectConditionalClauses = (
  payload: RJSFSchema,
  root: Record<string, unknown>,
  collect: (clause: Record<string, unknown>) => void
) => {
  const allOfArray = Array.isArray(payload.allOf) ? payload.allOf : []
  for (const item of allOfArray) {
    const clause = (item || {}) as Record<string, unknown>
    const ref = clause['$ref'] as string | undefined
    collect(ref ? (resolveRef(ref, root) ?? clause) : clause)
  }

  const dependencies = payload.dependencies as
    Record<string, string[] | Record<string, unknown>> | undefined
  for (const dependency of Object.values(dependencies ?? {})) {
    if (Array.isArray(dependency)) continue
    const ref = dependency['$ref'] as string | undefined
    collect(ref ? (resolveRef(ref, root) ?? dependency) : dependency)
  }
}

/**
 * List the sub-properties of an object property as "parent.child" rows,
 * including properties and enum values from its conditional branches.
 */
const collectNestedProperties = (
  parentKey: string,
  def: Record<string, unknown>,
  root: Record<string, unknown>
): SchemaProperty[] => {
  if (def['type'] !== 'object') return []

  const nested = new Map<string, SchemaProperty>()
  const props = (def['properties'] ?? {}) as Record<
    string,
    Record<string, unknown>
  >
  for (const [key, rawDef] of Object.entries(props)) {
    const resolved = resolveDef(rawDef, root)
    nested.set(key, {
      propertyKey: key,
      title: resolved['title'] as string | undefined,
      type: resolved['type'] as string | undefined,
      enumValues: extractEnumValues(resolved)
    })
  }

  collectConditionalClauses(def as RJSFSchema, root, (clause) => {
    collectConditionalProperties(clause, root, nested)
  })

  return Array.from(nested.values()).map((property) => ({
    ...property,
    propertyKey: `${parentKey}.${property.propertyKey}`
  }))
}

/**
 * Parse schemaJson and navigate to the subSamples.items.properties.payload
 * node. Returns undefined if the path does not exist.
 */
const resolveSubSampleSchema = (
  schemaJson: string | undefined
):
  | { root: Record<string, unknown>; subPayload: Record<string, unknown> }
  | undefined => {
  if (schemaJson == null) return undefined
  let parsed
  try {
    parsed = JSON.parse(schemaJson)
  } catch {
    return undefined
  }
  const root = getSampleRegistrationSchema(parsed, false) as Record<
    string,
    unknown
  >
  const payload = (root?.properties as Record<string, unknown> | undefined)
    ?.payload as Record<string, unknown> | undefined
  if (payload == null) return undefined

  const subSamplesRaw = (
    payload.properties as Record<string, Record<string, unknown>> | undefined
  )?.subSamples
  if (subSamplesRaw == null) return undefined

  const subSamplesSchema = resolveDef(subSamplesRaw, root)
  const items = subSamplesSchema['items'] as Record<string, unknown> | undefined
  if (items == null) return undefined

  const itemsProps = items['properties'] as
    Record<string, Record<string, unknown>> | undefined
  if (itemsProps == null) return undefined

  const subPayload = itemsProps['payload'] as
    Record<string, unknown> | undefined
  if (subPayload == null) return undefined

  return { root, subPayload }
}

/**
 * Build a complete, flat list of properties sent with a requisition for a
 * given payload schema. Includes base properties (marked x-include-in-requisition)
 * and allOf conditional properties — all possible values shown, conditions ignored.
 *
 * This is the data source for SchemaPropertyList in provelab.
 */
export const buildSchemaPropertyList = (
  schemaJson: string
): SchemaProperty[] => {
  let parsed: RJSFSchema
  try {
    parsed = JSON.parse(schemaJson) as RJSFSchema
  } catch {
    return []
  }

  const rootSchema = getSampleRegistrationSchema(parsed, false)
  const payload = rootSchema?.properties?.payload
  if (!payload || typeof payload !== 'object') return []

  const root = rootSchema as Record<string, unknown>

  const baseProps = (payload.properties ?? {}) as Record<
    string,
    Record<string, unknown>
  >
  const result: SchemaProperty[] = []
  for (const [key, rawDef] of Object.entries(baseProps)) {
    const resolved = resolveDef(rawDef, root)
    if ((resolved['x-include-in-requisition'] as boolean | undefined) !== true)
      continue
    result.push({
      propertyKey: key,
      title: resolved['title'] as string | undefined,
      type: resolved['type'] as string | undefined,
      enumValues: extractEnumValues(resolved)
    })
    result.push(...collectNestedProperties(key, resolved, root))
  }

  const conditionalMap = new Map<string, SchemaProperty>()
  collectConditionalClauses(payload, root, (clause) => {
    collectConditionalProperties(clause, root, conditionalMap)
  })

  const baseKeys = new Set(result.map((f) => f.propertyKey))
  for (const property of conditionalMap.values()) {
    if (!baseKeys.has(property.propertyKey)) result.push(property)
  }

  return result
}

export const buildSubSamplePropertyList = (
  schemaJson: string | undefined
): SchemaProperty[] | undefined => {
  const props = resolveSubSamplePayloadProps(schemaJson)
  if (props == null || Object.keys(props).length === 0) return undefined
  return Object.entries(props).map(([key, def]) => ({
    propertyKey: key,
    title: def['title'] as string | undefined,
    type: def['type'] as string | undefined,
    enumValues: extractEnumValues(def)
  }))
}

/**
 * Build a self-contained, lab-facing payload schema suitable for download.
 * Filters out base properties not marked x-include-in-requisition: true,
 * promotes the payload to root level, and strips RJSF-internal keys (x-*, ui:).
 *
 * Accepts the PayloadSchema JSON string stored on ContractAnalysisType.
 */
export const buildRawPayloadSchema = (
  schemaJson: string
): RJSFSchema | undefined => {
  let parsed: RJSFSchema
  try {
    parsed = JSON.parse(schemaJson) as RJSFSchema
  } catch {
    return undefined
  }

  const rootSchema = getSampleRegistrationSchema(parsed, false)
  const payload = rootSchema?.properties?.payload
  if (!payload || typeof payload !== 'object') return undefined

  const root = rootSchema as Record<string, unknown>
  const rawBaseProps = (payload.properties ?? {}) as Record<
    string,
    Record<string, unknown>
  >
  const filteredProps = Object.fromEntries(
    Object.entries(rawBaseProps).filter(
      ([, raw]) =>
        (resolveDef(raw, root)['x-include-in-requisition'] as
          boolean | undefined) === true
    )
  )

  const groups: Record<string, Record<string, unknown>> = {}
  collectRefs(
    {
      properties: filteredProps,
      allOf: payload.allOf,
      dependencies: payload.dependencies
    },
    root,
    groups,
    new Set()
  )

  const filteredRequired =
    Array.isArray(payload.required) && payload.required.length > 0
      ? payload.required.filter((key) =>
          Object.prototype.hasOwnProperty.call(filteredProps, key)
        )
      : undefined

  const schema: Record<string, unknown> = {
    type: 'object',
    ...groups,
    properties: filteredProps,
    allOf: payload.allOf,
    dependencies: payload.dependencies
  }
  if (Array.isArray(filteredRequired) && filteredRequired.length > 0) {
    schema.required = filteredRequired
  }

  return stripInternalKeys(schema) as RJSFSchema
}

/**
 * Returns the resolved subSamples array schema as a formatted JSON string
 * suitable for display and download, with all $ref pointers inlined.
 */
export const buildRawSubSampleSchema = (
  schemaJson: string | undefined
): string | undefined => {
  const ctx = resolveSubSampleSchema(schemaJson)
  if (ctx == null) return undefined
  const { root, subPayload } = ctx

  const groups: Record<string, Record<string, unknown>> = {}
  collectRefs(subPayload, root, groups, new Set())

  return JSON.stringify(
    stripInternalKeys({ type: 'object', ...groups, ...subPayload }),
    null,
    2
  )
}

/**
 * Resolve items.properties.payload.properties of the subSamples array,
 * following $ref pointers against the root schema.
 */
export const resolveSubSamplePayloadProps = (
  schemaJson: string | undefined
): Record<string, Record<string, unknown>> | undefined => {
  const ctx = resolveSubSampleSchema(schemaJson)
  if (ctx == null) return undefined
  const rawProps = ctx.subPayload['properties'] as
    Record<string, Record<string, unknown>> | undefined
  if (rawProps == null) return undefined
  return Object.fromEntries(
    Object.entries(rawProps).map(([k, def]) => [k, resolveDef(def, ctx.root)])
  )
}

const buildExampleForDefinition = (
  definition: Record<string, unknown>
): unknown => {
  const oneOf = definition['oneOf'] as Array<{ const: unknown }> | undefined
  const enm = definition['enum'] as unknown[] | undefined
  const examples = definition['examples'] as unknown[] | undefined

  if (Array.isArray(oneOf) && oneOf.length > 0) return oneOf[0].const
  if (Array.isArray(enm) && enm.length > 0) return enm[0]
  if (Array.isArray(examples) && examples.length > 0) return examples[0]

  const type = definition['type']
  if (type === 'integer' || type === 'number') return 0
  if (type === 'boolean') return false
  if (type === 'object') return {}
  if (type === 'array') return []
  return ''
}

export const buildSubSamplePayloadExample = (
  schemaJson: string | undefined
): string | undefined => {
  const props = resolveSubSamplePayloadProps(schemaJson)
  if (props == null || Object.keys(props).length === 0) return undefined
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(props).map(([key, def]) => {
        let exampleValue = buildExampleForDefinition(def)

        if (def['type'] === 'array') {
          const items = def['items']
          exampleValue =
            items != null && typeof items === 'object'
              ? [buildExampleForDefinition(items as Record<string, unknown>)]
              : []
        }

        return [key, exampleValue]
      })
    )
  )
}

export const buildMetadataExample = (
  schemaJson: string | undefined
): string | undefined => {
  if (schemaJson == null) return undefined
  const props = buildRawPayloadSchema(schemaJson)?.properties as
    Record<string, Record<string, unknown>> | undefined
  if (props == null || Object.keys(props).length === 0) return undefined
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(props).map(([k, def]) => [
        k,
        buildExampleForDefinition(def)
      ])
    )
  )
}
